import React, { useState, useEffect } from 'react';

const ItemModal = ({ item, onClose, onAddToCart }) => {
    const [imageError, setImageError] = useState(false);
    const [quantity, setQuantity] = useState(1);

    // Reset quantity when modal opens for a new item
    useEffect(() => {
        if (item) {
            setQuantity(1);
            setImageError(false);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [item]);

    if (!item) return null;

    const fallbackImage = 'https://via.placeholder.com/400x300?text=No+Image';
    const imageUrl = item.image && !imageError ? item.image : fallbackImage;
    const itemPrice = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;

    return (
        /* OVERLAY: Standardized high z-index and fixed viewport positioning */
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-300 animate-fade-in">
            {/* Backdrop click area */}
            <div className="absolute inset-0 z-[-1]" onClick={onClose}></div>

            {/* MODAL BOX: Standardized max-height and flex-col for centering */}
            <div
                className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative flex flex-col max-h-[90vh] animate-slide-up"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 bg-white/80 backdrop-blur-md rounded-full p-2 text-gray-800 hover:bg-white hover:text-red-500 transition-all z-20 shadow-md"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Hero Image */}
                <div className="w-full h-64 relative bg-gray-100 shrink-0">
                    <img
                        src={imageUrl}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={() => setImageError(true)}
                    />
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent pointer-events-none"></div>
                </div>

                {/* Content: Scrollable if description is long */}
                <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
                    <div className="flex justify-between items-start gap-4 mb-4">
                        <h2 className="text-2xl font-black text-gray-800 tracking-tight leading-tight">{item.name}</h2>
                        <span className="text-2xl font-black text-amber-600 shrink-0">
                            {itemPrice.toFixed(2)} ETB
                        </span>
                    </div>

                    <div className="mb-6">
                        {item.category && (
                            <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-full mb-4 uppercase tracking-wider">
                                {item.category}
                            </span>
                        )}
                        <p className="text-gray-600 text-base leading-relaxed font-medium">
                            {item.details || "Taste the excellence in every bite of our chef's special creation."}
                        </p>
                    </div>
                </div>

                {/* Actions: Always visible at bottom */}
                <div className="p-6 border-t border-gray-100 bg-gray-50/50 shrink-0">
                    <div className="flex items-center gap-4">
                        {/* Quantity Selector */}
                        <div className="flex items-center bg-white border border-gray-200 rounded-2xl p-1 shadow-sm shrink-0">
                            <button
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                className="w-12 h-12 flex items-center justify-center rounded-xl text-gray-600 hover:bg-amber-50 hover:text-amber-600 transition-all active:scale-95"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" /></svg>
                            </button>
                            <span className="w-8 text-center font-black text-gray-900 text-lg">{quantity}</span>
                            <button
                                onClick={() => setQuantity(quantity + 1)}
                                className="w-12 h-12 flex items-center justify-center rounded-xl text-gray-600 hover:bg-amber-50 hover:text-amber-600 transition-all active:scale-95"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" /></svg>
                            </button>
                        </div>

                        {/* Action Button */}
                        <button
                            onClick={() => onAddToCart && onAddToCart(item, quantity)}
                            className="flex-1 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-2xl font-black text-lg transition-all flex justify-center items-center gap-2 shadow-xl shadow-amber-600/30 active:scale-[0.98]"
                        >
                            Add to Cart • {(itemPrice * quantity).toFixed(2)} ETB
                        </button>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
                .animate-fade-in { animation: fade-in 0.3s ease-out; }
                
                @keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                .animate-slide-up { animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1); }

                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
            `}</style>
        </div>
    );
};

export default ItemModal;
